
























































































































































































































































































































































































































































































































































import axios from 'axios';
import gql from 'graphql-tag';
import moment from 'moment-timezone';
import Component from 'vue-class-component';

import { BaseVue } from '@/BaseVue';
import UiButton from '@/components/ui/UiButton.vue';
import UiCheckbox from '@/components/ui/UiCheckbox.vue';
import UiDatePicker from '@/components/ui/UiDatePicker.vue';
import UiTooltip from '@/components/ui/UiTooltip.vue';
import { MUT_SNACKBAR } from '@/store';
import { getClusterAwareBaseUrl } from '@/utils/clusterAwareUrl';
import { getSymbolForCurrency } from '@/utils/coinUtils';

import { baConfig } from '../../../config';

interface BalanceCheckLine {
  wallet_name?: string;
  asset_symbol?: string;
  bitwave_balance?: number | string;
  bitwave_register_balance?: number | string;
  bitwave_raw_balance?: number | string;
  thirdparty_balance?: number | string;
  register_difference?: number | string;
  raw_difference?: number | string;
  difference?: number | string;
  bitwave_balance_time?: string | { value: string };
  thirdparty_balance_time?: string | { value: string };
}

/**
 * Decorated row used by the table template: precomputes per-row values that
 * would otherwise be derived by repeated method calls during render (and
 * thus rebuilt on every keystroke, popover toggle, sort flip, etc.).
 */
interface BalanceCheckRow {
  line: BalanceCheckLine;
  connection: { label: string; kind: 'network' | 'exchange' | 'other' };
  isMismatch: boolean;
  hasThirdParty: boolean;
  dollarVariance: number | null;
}

interface BalanceCheckColumn {
  heading: string;
  align?: 'left' | 'right';
  tooltip?: string;
  /** When present, the column header is clickable and sorts by this key. */
  sortKey?: string;
}

@Component({
  components: {
    UiButton,
    UiCheckbox,
    UiDatePicker,
    UiTooltip,
  },
  apollo: {
    /**
     * Wallets including the connection IDs we need to derive the Connection
     * pill. The shared `wallets/getWallets` store action doesn't request
     * `connectionId` / `exchangeConnectionId`, so without this query the
     * Connection column only worked for on-chain wallets (which surface via
     * `networkId`) and never for exchange wallets like Coinbase or Kraken.
     */
    walletsForConnections: {
      query: gql`
        query GetWalletsForBalanceCheck($orgId: ID!) {
          wallets(orgId: $orgId) {
            id
            name
            networkId
            connectionId
            exchangeConnectionId
          }
        }
      `,
      variables(this: any) {
        return { orgId: this.$store.state.currentOrg.id };
      },
      update(data: any) {
        return data?.wallets ?? [];
      },
      skip(this: any) {
        return !this.$store.state.currentOrg?.id;
      },
    },
    /** Connections list — we map id → provider to label exchange wallets. */
    orgConnections: {
      query: gql`
        query GetConnectionsForBalanceCheck($orgId: ID!) {
          connections(orgId: $orgId, overrideCache: true) {
            id
            provider
          }
        }
      `,
      variables(this: any) {
        return { orgId: this.$store.state.currentOrg.id };
      },
      update(data: any) {
        return data?.connections ?? [];
      },
      skip(this: any) {
        return !this.$store.state.currentOrg?.id;
      },
    },
  },
})
export default class InventoryBalanceCheckReport extends BaseVue {
  // Set via the `created()` lifecycle hook below so the default respects the
  // org's configured timezone via `getOrgMoment()` rather than the browser tz.
  public compareDate = '';
  public isLoading = false;

  /**
   * Monotonically-increasing request id. Bumped by every `handleRunReport`
   * call and by `handleCancelReport`. The run aborts state-commit if the
   * id changed mid-flight — a lightweight cancel without needing
   * AbortController plumbing on every fetch.
   */
  private runRequestId = 0;

  /**
   * Cached collator for all string comparisons. Constructing this once
   * and reusing `.compare` is 10–50× faster than calling
   * `String.prototype.localeCompare` per pair — the latter lazily
   * instantiates a fresh `Intl.Collator` on every call. With 5k+ rows the
   * default sort runs ~60k comparisons; localeCompare was the dominant
   * cost behind the 2–3 second filter-toggle lag.
   *
   * `sensitivity: 'base'` matches the prior case-folded behaviour
   * (wallet/asset/connection sort keys were already lower-cased before
   * being passed in).
   */
  private static readonly collator = new Intl.Collator(undefined, { sensitivity: 'base' });

  /**
   * Multi-stage progress for the loading placeholder. Each stage has its
   * own status (pending → active → done) so concurrent work (spam +
   * pricing) shows as two simultaneously-active rows. The order in this
   * array is the display order; we mutate `status`/`detail`/`progress`
   * in place during the pipeline.
   */
  public loadingStages: Array<{
    key: 'balanceCheck' | 'spam' | 'pricing';
    label: string;
    status: 'pending' | 'active' | 'done';
    detail?: string;
    progress?: { done: number; total: number };
  }> = [];

  /** Index of the currently displayed rotating tip. */
  public loadingTipIndex = 0;
  /** Interval handle for cycling tips while the report runs. */
  private tipInterval: number | null = null;

  /**
   * Rotating one-liners shown at the bottom of the loading placeholder.
   * They cycle every few seconds so the user has something to read while
   * the report runs.
   */
  public readonly loadingTips: readonly string[] = [
    'Looking up each ticker in the spam registry — large reports can take a minute…',
    'Pricing data powers the $ Variance column…',
    'Connection labels are abbreviated in the table — hover for the full name…',
    'Filter by wallet, asset, or connection using the icon next to each column header.',
    'Use the page size selector under the table to render more rows at once.',
  ];

  public showMismatchesOnly = false;
  public hideSpam = true;
  public hideZeroBalances = true;
  /**
   * Whether the inline "Advanced" disclosure (containing the three
   * filter checkboxes) is expanded. Starts collapsed; clicking the
   * Advanced button grows the settings card downward.
   */
  public expandAdvanced = false;
  /** Selected wallet names. Empty array means "no filter" (show all). */
  public walletFilters: string[] = [];
  /** Selected asset symbols. Empty array means "no filter" (show all). */
  public assetFilters: string[] = [];
  /**
   * Selected connection labels (full form, e.g. "ETHEREUM", "COINBASE").
   * We store the full label — not the abbreviated pill text — so the filter
   * stays semantically meaningful regardless of display abbreviation.
   */
  public connectionFilters: string[] = [];
  /** Sort key of the column whose filter popover is currently open. */
  public openFilter: string | null = null;
  /** Inline `style` for the open popover (fixed-positioned to escape table overflow). */
  public filterPopoverStyle: Record<string, string> = {};
  /** Search-within-list text for the open filter popover. */
  public filterSearch = '';
  /** Column sort key (undefined = default sort: wallet asc, asset asc). */
  public sortKey: string | null = null;
  public sortDir: 'asc' | 'desc' = 'asc';

  /**
   * Pagination. Rendering 5k+ <tr>s at once causes Vue to re-diff a
   * 45k-node subtree on every filter keystroke / popover toggle, which
   * is the dominant source of UI jank. We slice `visibleRows` into pages
   * so only `pageSize` rows are mounted at a time. Footer totals and CSV
   * export still use the full filtered set.
   */
  public pageSize = 250;
  public currentPage = 1;
  public readonly pageSizeOptions: number[] = [100, 250, 500, 1000];

  public baseUrl = getClusterAwareBaseUrl(process.env.VUE_APP_RPT_API_URL ?? process.env.VUE_APP_API_URL!);
  public balanceCheckReport: { lines?: BalanceCheckLine[] } | null = null;
  /** Populated by the apollo smart query. */
  public walletsForConnections: any[] = [];
  /** Populated by the apollo smart query. */
  public orgConnections: any[] = [];
  public reportCSV = '';
  public reportElapsedTime: string | null = null;
  public errorMessage = '';
  public generatedAt = '';
  /** Set of lower-cased tickers flagged as spam (spamScore > 0) by address-svc. */
  private spamTickers: Set<string> | null = null;
  /** Map of lower-cased ticker -> unit price (org base currency) for compareDate. */
  private unitPrices: Map<string, number> | null = null;

  public columns: BalanceCheckColumn[] = [
    { heading: 'Wallet', sortKey: 'wallet' },
    { heading: 'Connection', sortKey: 'connection' },
    { heading: 'Asset', sortKey: 'asset' },
    { heading: 'Bitwave Balance', align: 'right', sortKey: 'bitwave' },
    { heading: 'Third-Party Balance', align: 'right', sortKey: 'thirdparty' },
    { heading: 'Difference', align: 'right', sortKey: 'difference' },
    {
      heading: '$ Variance',
      align: 'right',
      sortKey: 'variance',
      tooltip:
        'Token difference priced in your org’s base currency at the as-of date, using the Balance Report’s unit price.',
    },
    {
      heading: 'Bitwave Balance Time',
      sortKey: 'bitwave_time',
      tooltip: 'Date and time are shown in your organization’s selected time zone',
    },
    {
      heading: 'Third-Party Balance Time',
      sortKey: 'thirdparty_time',
      tooltip: 'Date and time are shown in your organization’s selected time zone',
    },
  ];

  public get fiatSymbol(): string {
    const code = this.$store.state.currentOrg?.baseCurrency;
    return code ? getSymbolForCurrency(code) : '$';
  }

  created() {
    // Default the compare date to yesterday in the org's configured timezone.
    // `getOrgMoment()` (from BaseVue) returns a moment anchored to the org tz
    // so the previous-day calculation respects the org's accounting timezone
    // rather than the browser's local tz.
    this.compareDate = this.getOrgMoment().subtract(1, 'day').format('YYYY-MM-DD');
  }

  public get entityName(): string {
    return this.$store.state.currentOrg?.name ?? '';
  }

  public get isValid(): boolean {
    return this.isValidDate(this.compareDate);
  }

  /**
   * Connection id → provider name (e.g. 'coinbase', 'kraken'). Built from
   * the `connections` query so we can resolve an exchange wallet's brand.
   */
  private get connectionProviderById(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const c of this.orgConnections) {
      if (c?.id && c?.provider) out[c.id] = c.provider;
    }
    return out;
  }

  /**
   * Wallet name → connection pill data. Exchange wallets are labelled by
   * their connection's `provider` (Coinbase, Kraken, …); on-chain wallets
   * fall back to `networkId`. Matches the resolution used by Wallets.vue.
   */
  public get walletConnectionByName(): Record<string, { label: string; kind: 'network' | 'exchange' | 'other' }> {
    const out: Record<string, { label: string; kind: 'network' | 'exchange' | 'other' }> = {};
    const byId = this.connectionProviderById;
    for (const w of this.walletsForConnections) {
      if (!w?.name) continue;
      const exchangeProvider = byId[w.exchangeConnectionId ?? ''] || byId[w.connectionId ?? ''];
      if (exchangeProvider) {
        // Store full labels in UPPERCASE for consistency with on-chain
        // networks (ETHEREUM, etc.) — the table pill abbreviates for
        // display while filters and CSV use the full form.
        out[w.name] = { label: String(exchangeProvider).toUpperCase(), kind: 'exchange' };
      } else if (w.networkId) {
        out[w.name] = { label: String(w.networkId).toUpperCase(), kind: 'network' };
      } else {
        out[w.name] = { label: '—', kind: 'other' };
      }
    }
    return out;
  }

  public getConnection(line: BalanceCheckLine): { label: string; kind: 'network' | 'exchange' | 'other' } {
    if (!line.wallet_name) return { label: '—', kind: 'other' };
    return this.walletConnectionByName[line.wallet_name] ?? { label: '—', kind: 'other' };
  }

  private titleCase(s: unknown): string {
    if (typeof s !== 'string' || !s) return '';
    return s
      .toLowerCase()
      .replace(/(^|[_\s-])(\w)/g, (_, sep, ch) => (sep ? ' ' : '') + ch.toUpperCase())
      .trim();
  }

  /**
   * Map of full connection label (UPPER, alphanumerics only) -> short
   * display abbreviation. Anything not in this map AND longer than 6 chars
   * falls back to the first 3 characters uppercased.
   *
   * Labels <= 6 chars (e.g. KRAKEN, GEMINI, BTC, OP) are shown verbatim.
   */
  private static readonly CONNECTION_ABBREVIATIONS: Record<string, string> = {
    ETHEREUM: 'ETH',
    BITCOIN: 'BTC',
    POLYGON: 'POLY',
    ARBITRUM: 'ARB',
    OPTIMISM: 'OP',
    AVALANCHE: 'AVAX',
    SOLANA: 'SOL',
    COINBASE: 'CB',
    COINBASEPRIME: 'CBP',
    COINBASEEXCHANGE: 'CBEX',
    BINANCE: 'BNB',
    BITSTAMP: 'BS',
  };

  /**
   * Short pill text for a connection label. The full label is preserved
   * for the title tooltip, filter dropdown, and CSV — only the table pill
   * uses the abbreviated form.
   */
  public abbreviateConnection(label: string): string {
    if (!label || label === '—') return label;
    const key = label.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const known = InventoryBalanceCheckReport.CONNECTION_ABBREVIATIONS[key];
    if (known) return known;
    if (label.length <= 6) return label;
    return label.slice(0, 3).toUpperCase();
  }

  /**
   * Pre-decorated rows for the table. Building this once per dependency
   * change avoids calling `getConnection`, `isMismatch`, `hasThirdParty`,
   * `getDollarVariance` 3–5 times per row during every render — which was
   * the cause of the perceived lag on filter clicks / popover toggles.
   */
  public get visibleRows(): BalanceCheckRow[] {
    const connByName = this.walletConnectionByName;
    return this.visibleLines.map((line) => {
      const hasThirdParty = this.hasThirdPartyBalance(line);
      return {
        line,
        connection: (line.wallet_name && connByName[line.wallet_name]) || { label: '—', kind: 'other' },
        isMismatch: this.isMismatch(line),
        hasThirdParty,
        dollarVariance: hasThirdParty ? this.getDollarVariance(line) : null,
      };
    });
  }

  /** Total pages for the current filtered set (>= 1). */
  public get totalPages(): number {
    const n = this.visibleRows.length;
    return Math.max(1, Math.ceil(n / this.pageSize));
  }

  /** Page-clamped current page (in case data shrinks below currentPage). */
  public get safeCurrentPage(): number {
    return Math.min(Math.max(1, this.currentPage), this.totalPages);
  }

  /**
   * The slice of `visibleRows` actually mounted in the DOM. Keeping this
   * to <= `pageSize` rows is what makes filter/sort interactions snappy
   * on large reports — see the comment on `pageSize`.
   */
  public get pagedRows(): BalanceCheckRow[] {
    const start = (this.safeCurrentPage - 1) * this.pageSize;
    return this.visibleRows.slice(start, start + this.pageSize);
  }

  /** 1-based index of the first visible row on the current page. */
  public get pageRangeStart(): number {
    if (!this.visibleRows.length) return 0;
    return (this.safeCurrentPage - 1) * this.pageSize + 1;
  }

  /** 1-based index of the last visible row on the current page. */
  public get pageRangeEnd(): number {
    return Math.min(this.visibleRows.length, this.safeCurrentPage * this.pageSize);
  }

  public goToPage(page: number): void {
    this.currentPage = Math.min(Math.max(1, Math.floor(page)), this.totalPages);
  }

  public onPageSizeChange(size: number): void {
    // Anchor on the first row currently visible so the user keeps their place.
    const anchor = (this.safeCurrentPage - 1) * this.pageSize;
    this.pageSize = size;
    this.currentPage = Math.floor(anchor / size) + 1;
  }

  /** Template-friendly wrapper that avoids inline TS casts. */
  public onPageSizeChangeEvent(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const size = Number(target?.value);
    if (Number.isFinite(size) && size > 0) this.onPageSizeChange(size);
  }

  /**
   * Reset to page 1 — called from every UI handler that mutates the
   * filter/sort state. Inlined (rather than via @Watch) to avoid pulling
   * in vue-property-decorator and to keep the data flow obvious.
   */
  private resetPage(): void {
    this.currentPage = 1;
  }

  /**
   * Sorted + filtered lines used to render the table.
   *
   * Performance notes:
   *  - Single-pass filter: a chained `.filter().filter()...` allocates a
   *    fresh array per stage. On 5k+ rows with all toggles + column
   *    filters active that's 5–6 intermediate arrays per recompute. We
   *    instead evaluate every predicate inline in one loop.
   *  - Decorate-sort-undecorate: the comparator is called O(n log n)
   *    times, so we precompute each row's sort key *once* and sort an
   *    auxiliary `{ line, key }` array. This is what removed the
   *    perceived lag on the Connection column specifically — previously
   *    every comparison re-resolved the connection label via dict
   *    lookup + `.toLowerCase()`.
   */
  public get visibleLines(): BalanceCheckLine[] {
    const all = this.balanceCheckReport?.lines ?? [];
    const spam = this.hideSpam ? this.spamTickers : null;
    const hideZero = this.hideZeroBalances;
    const mismatchOnly = this.showMismatchesOnly;
    const walletSel = this.walletFilters.length ? new Set(this.walletFilters) : null;
    const assetSel = this.assetFilters.length ? new Set(this.assetFilters) : null;
    const connectionSel = this.connectionFilters.length ? new Set(this.connectionFilters) : null;
    const connByName = connectionSel ? this.walletConnectionByName : null;

    const lines: BalanceCheckLine[] = [];
    for (let i = 0; i < all.length; i++) {
      const l = all[i];
      if (spam && spam.has((l.asset_symbol ?? '').toLowerCase().trim())) continue;
      if (hideZero && this.isBothBalancesZero(l)) continue;
      if (mismatchOnly && !this.isMismatch(l)) continue;
      if (walletSel && !walletSel.has(l.wallet_name ?? '')) continue;
      if (assetSel && !assetSel.has(l.asset_symbol ?? '')) continue;
      if (connectionSel && connByName && !connectionSel.has(connByName[l.wallet_name ?? '']?.label ?? '')) continue;
      lines.push(l);
    }

    // Rows without a third-party balance always sink to the bottom,
    // regardless of the active sort. Users are looking for actionable
    // mismatches; "no third-party data" rows are noise that should never
    // push real comparisons off-screen.
    const cmp = InventoryBalanceCheckReport.collator.compare;

    if (this.sortKey) {
      // Decorate: precompute (naRank, sortValue) per row.
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      type Decorated = { line: BalanceCheckLine; na: number; v: string | number | null };
      const decorated: Decorated[] = new Array(lines.length);
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i];
        decorated[i] = {
          line: l,
          na: this.hasThirdPartyBalance(l) ? 0 : 1,
          v: this.sortValue(l, key),
        };
      }
      decorated.sort((a, b) => {
        if (a.na !== b.na) return a.na - b.na;
        const av = a.v;
        const bv = b.v;
        const aMissing = av === null;
        const bMissing = bv === null;
        if (aMissing && bMissing) return 0;
        if (aMissing) return 1;
        if (bMissing) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        return cmp(String(av), String(bv)) * dir;
      });
      for (let i = 0; i < decorated.length; i++) lines[i] = decorated[i].line;
    } else {
      // Default sort: N/A last, then wallet name, then asset symbol — also
      // decorated so each row's keys are read once, not per comparison.
      type Decorated = { line: BalanceCheckLine; na: number; w: string; a: string };
      const decorated: Decorated[] = new Array(lines.length);
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i];
        decorated[i] = {
          line: l,
          na: this.hasThirdPartyBalance(l) ? 0 : 1,
          w: l.wallet_name ?? '',
          a: l.asset_symbol ?? '',
        };
      }
      decorated.sort((a, b) => {
        if (a.na !== b.na) return a.na - b.na;
        const aw = cmp(a.w, b.w);
        if (aw !== 0) return aw;
        return cmp(a.a, b.a);
      });
      for (let i = 0; i < decorated.length; i++) lines[i] = decorated[i].line;
    }

    return lines;
  }

  /** Columns that expose an inline filter icon in their header. */
  public isFilterableCol(col: BalanceCheckColumn): boolean {
    return col.sortKey === 'wallet' || col.sortKey === 'asset' || col.sortKey === 'connection';
  }

  /** True when the column's filter currently has a non-empty value. */
  public hasFilterValue(col: BalanceCheckColumn): boolean {
    if (col.sortKey === 'wallet') return this.walletFilters.length > 0;
    if (col.sortKey === 'asset') return this.assetFilters.length > 0;
    if (col.sortKey === 'connection') return this.connectionFilters.length > 0;
    return false;
  }

  /** Currently-selected values for the given column. */
  private selectedFor(col: BalanceCheckColumn): string[] {
    if (col.sortKey === 'wallet') return this.walletFilters;
    if (col.sortKey === 'asset') return this.assetFilters;
    if (col.sortKey === 'connection') return this.connectionFilters;
    return [];
  }

  /**
   * Lines that survive the global filters (hide spam / hide zero /
   * mismatches-only) but not the column-specific wallet/asset filters.
   * Computed once per dependency change — reused by both filter dropdowns
   * (and incidentally by `visibleLines` if we ever inline it there).
   */
  private get filterBaseLines(): BalanceCheckLine[] {
    const all = this.balanceCheckReport?.lines ?? [];
    const spam = this.hideSpam ? this.spamTickers : null;
    const hideZero = this.hideZeroBalances;
    const mismatchOnly = this.showMismatchesOnly;
    if (!spam && !hideZero && !mismatchOnly) return all;
    const out: BalanceCheckLine[] = [];
    for (let i = 0; i < all.length; i++) {
      const l = all[i];
      if (spam && spam.has((l.asset_symbol ?? '').toLowerCase().trim())) continue;
      if (hideZero && this.isBothBalancesZero(l)) continue;
      if (mismatchOnly && !this.isMismatch(l)) continue;
      out.push(l);
    }
    return out;
  }

  /**
   * Apply every column filter EXCEPT the one named in `exclude` to
   * `filterBaseLines`. Used by each column's option list so its dropdown
   * always reflects "what would be available after the other columns'
   * filters are applied". Single-pass for the same reason `visibleLines`
   * is single-pass: chained `.filter()` calls were allocating multiple
   * intermediate arrays per recompute.
   */
  private linesForOptions(exclude: 'wallet' | 'asset' | 'connection'): BalanceCheckLine[] {
    const base = this.filterBaseLines;
    const walletSel = exclude !== 'wallet' && this.walletFilters.length ? new Set(this.walletFilters) : null;
    const assetSel = exclude !== 'asset' && this.assetFilters.length ? new Set(this.assetFilters) : null;
    const connectionSel =
      exclude !== 'connection' && this.connectionFilters.length ? new Set(this.connectionFilters) : null;
    if (!walletSel && !assetSel && !connectionSel) return base;
    const connByName = connectionSel ? this.walletConnectionByName : null;
    const out: BalanceCheckLine[] = [];
    for (let i = 0; i < base.length; i++) {
      const l = base[i];
      if (walletSel && !walletSel.has(l.wallet_name ?? '')) continue;
      if (assetSel && !assetSel.has(l.asset_symbol ?? '')) continue;
      if (connectionSel && connByName && !connectionSel.has(connByName[l.wallet_name ?? '']?.label ?? '')) continue;
      out.push(l);
    }
    return out;
  }

  /** Distinct, sorted wallet names that should appear in the wallet filter. */
  private get walletFilterOptionsBase(): string[] {
    return this.distinctSorted(this.linesForOptions('wallet'), 'wallet_name');
  }

  /** Distinct, sorted asset symbols that should appear in the asset filter. */
  private get assetFilterOptionsBase(): string[] {
    return this.distinctSorted(this.linesForOptions('asset'), 'asset_symbol');
  }

  /** Distinct, sorted connection labels (full form) for the connection filter. */
  private get connectionFilterOptionsBase(): string[] {
    const connByName = this.walletConnectionByName;
    const seen = new Set<string>();
    const out: string[] = [];
    for (const l of this.linesForOptions('connection')) {
      const label = connByName[l.wallet_name ?? '']?.label ?? '';
      if (!label || label === '—' || seen.has(label)) continue;
      seen.add(label);
      out.push(label);
    }
    return out.sort(InventoryBalanceCheckReport.collator.compare);
  }

  /**
   * Options shown in the currently-open filter popover, narrowed by the
   * search box. Templates bind to this single computed instead of calling
   * a method per render — prevents the dropdown from rewalking every line
   * on each keystroke.
   */
  public get currentFilterOptions(): string[] {
    if (!this.openFilter) return [];
    let base: string[];
    if (this.openFilter === 'wallet') base = this.walletFilterOptionsBase;
    else if (this.openFilter === 'asset') base = this.assetFilterOptionsBase;
    else if (this.openFilter === 'connection') base = this.connectionFilterOptionsBase;
    else base = [];
    const q = this.filterSearch.trim().toLowerCase();
    return q ? base.filter((v) => v.toLowerCase().includes(q)) : base;
  }

  /** Distinct, locale-sorted values of `field` across `lines`. */
  private distinctSorted(lines: BalanceCheckLine[], field: keyof BalanceCheckLine): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const l of lines) {
      const v = (l[field] as string | undefined) ?? '';
      if (!v || seen.has(v)) continue;
      seen.add(v);
      out.push(v);
    }
    return out.sort(InventoryBalanceCheckReport.collator.compare);
  }

  public isFilterValueSelected(col: BalanceCheckColumn, value: string): boolean {
    return this.selectedFor(col).includes(value);
  }

  /** Toggle a single value on/off in the column's selected set. */
  public toggleFilterValue(col: BalanceCheckColumn, value: string): void {
    const current = this.selectedFor(col);
    const idx = current.indexOf(value);
    const next = idx === -1 ? [...current, value] : current.filter((v) => v !== value);
    if (col.sortKey === 'wallet') this.walletFilters = next;
    else if (col.sortKey === 'asset') this.assetFilters = next;
    else if (col.sortKey === 'connection') this.connectionFilters = next;
    this.resetPage();
  }

  /** Clear all selected values for a column. */
  public clearColumnFilter(col: BalanceCheckColumn): void {
    if (col.sortKey === 'wallet') this.walletFilters = [];
    else if (col.sortKey === 'asset') this.assetFilters = [];
    else if (col.sortKey === 'connection') this.connectionFilters = [];
    this.resetPage();
  }

  /** Open the filter popover for a column, or close it if it's already open. */
  public toggleFilterPopover(key: string | undefined, event?: MouseEvent): void {
    if (!key) return;
    this.openFilter = this.openFilter === key ? null : key;
    this.filterSearch = '';
    if (this.openFilter && event) {
      // Anchor the popover to the icon button using viewport coords so it
      // escapes the table's overflow-hidden / overflow-x-auto wrappers.
      const btn = event.currentTarget as HTMLElement | null;
      const rect = btn?.getBoundingClientRect();
      const popoverWidth = 240; // tw-w-60 = 15rem = 240px
      if (rect) {
        // Right-align to the icon (icon sits at the right side of right-aligned
        // columns), then clamp to viewport.
        let left = rect.right - popoverWidth;
        if (left < 8) left = 8;
        if (left + popoverWidth > window.innerWidth - 8) {
          left = window.innerWidth - popoverWidth - 8;
        }
        this.filterPopoverStyle = {
          top: `${rect.bottom + 4}px`,
          left: `${left}px`,
        };
      } else {
        this.filterPopoverStyle = {};
      }
      this.$nextTick(() => {
        const refs = this.$refs.filterInput as HTMLInputElement[] | HTMLInputElement | undefined;
        const el = Array.isArray(refs) ? refs[0] : refs;
        el?.focus();
      });
    }
  }

  /**
   * Close the popover when the user mousedowns outside of it (and outside
   * the filter icon that opened it). Using mousedown instead of click means
   * the popover dismisses the instant the user presses anywhere else —
   * including on table cells, the page header, or the date picker. We use
   * `composedPath()` to detect the icon button by its data attribute so we
   * don't close on the toggle that just opened it.
   */
  private onDocumentMousedown = (e: MouseEvent): void => {
    if (!this.openFilter) return;
    const refs = this.$refs.filterPopover as HTMLElement[] | HTMLElement | undefined;
    const popoverEl = Array.isArray(refs) ? refs[0] : refs;
    const target = e.target as Node | null;
    if (popoverEl && target && popoverEl.contains(target)) return;
    const path = (typeof e.composedPath === 'function' ? e.composedPath() : []) as EventTarget[];
    for (const el of path) {
      if (el instanceof HTMLElement && el.dataset.filterIconFor === this.openFilter) return;
    }
    this.openFilter = null;
  };

  /** Close any open filter popover. */
  public closeFilterPopover(): void {
    this.openFilter = null;
  }

  /**
   * Backdrop mousedown handler. Always closes the current popover. If the
   * press happened over a filter icon for a *different* column, we also
   * open that column's popover in the same tick — so the user doesn't
   * have to click twice to switch filters. We determine the underlying
   * element by temporarily hiding the backdrop and using
   * `elementFromPoint`.
   */
  public onBackdropMousedown(event: MouseEvent): void {
    const backdrop = event.currentTarget as HTMLElement | null;
    let underlying: Element | null = null;
    if (backdrop) {
      const prev = backdrop.style.pointerEvents;
      backdrop.style.pointerEvents = 'none';
      underlying = document.elementFromPoint(event.clientX, event.clientY);
      backdrop.style.pointerEvents = prev;
    }

    // Walk up looking for a filter-icon button so we can hand off the
    // open intent for that column.
    let target: Element | null = underlying;
    let nextKey: string | null = null;
    while (target && target !== document.body) {
      if (target instanceof HTMLElement && target.dataset.filterIconFor) {
        nextKey = target.dataset.filterIconFor;
        break;
      }
      target = target.parentElement;
    }

    if (nextKey && nextKey !== this.openFilter) {
      // Switching to a different column's filter: simulate a click on its
      // icon so the popover positions itself correctly.
      const buttons = document.querySelectorAll<HTMLElement>(`[data-filter-icon-for="${nextKey}"]`);
      const btn = buttons[0];
      if (btn) {
        this.openFilter = null;
        this.$nextTick(() => btn.click());
        return;
      }
    }

    this.openFilter = null;
  }

  /**
   * Indicator glyph for a column header sort affordance. Mirrors the
   * v3 BalancesTable convention: ⇅ when the column isn't the active
   * sort, ▲ ascending, ▼ descending.
   */
  public sortGlyph(key: string): string {
    if (this.sortKey !== key) return '⇅';
    return this.sortDir === 'desc' ? '▼' : '▲';
  }

  /**
   * Toggle the column sort. First click on a new column sorts ascending;
   * clicking the active column flips direction; a third click on a desc
   * column clears back to the default wallet/asset sort.
   */
  public toggleSort(key: string): void {
    this.resetPage();
    if (this.sortKey !== key) {
      this.sortKey = key;
      this.sortDir = 'asc';
      return;
    }
    if (this.sortDir === 'asc') {
      this.sortDir = 'desc';
      return;
    }
    this.sortKey = null;
    this.sortDir = 'asc';
  }

  /** Compare two lines by the given sort key. Missing values sort last. */
  private compareByKey(a: BalanceCheckLine, b: BalanceCheckLine, key: string): number {
    const av = this.sortValue(a, key);
    const bv = this.sortValue(b, key);
    // Sentinel: undefined/NaN sorts after defined values, regardless of dir.
    const aMissing = av === null;
    const bMissing = bv === null;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return InventoryBalanceCheckReport.collator.compare(String(av), String(bv));
  }

  /** Comparable value for a row + sort key, or null when missing. */
  private sortValue(line: BalanceCheckLine, key: string): string | number | null {
    switch (key) {
      case 'wallet':
        return (line.wallet_name ?? '').toLowerCase() || null;
      case 'connection':
        return this.getConnection(line).label.toLowerCase() || null;
      case 'asset':
        return (line.asset_symbol ?? '').toLowerCase() || null;
      case 'bitwave': {
        const n = this.toNum(line.bitwave_register_balance ?? line.bitwave_balance);
        return Number.isFinite(n) ? n : null;
      }
      case 'thirdparty': {
        if (!this.hasThirdPartyBalance(line)) return null;
        const n = this.toNum(line.thirdparty_balance);
        return Number.isFinite(n) ? n : null;
      }
      case 'difference': {
        if (!this.hasThirdPartyBalance(line)) return null;
        const n = this.toNum(line.register_difference ?? line.difference);
        return Number.isFinite(n) ? n : null;
      }
      case 'variance':
        return this.getDollarVariance(line);
      case 'bitwave_time':
        return this.getTimeValue(line.bitwave_balance_time) || null;
      case 'thirdparty_time':
        return this.getTimeValue(line.thirdparty_balance_time) || null;
      default:
        return null;
    }
  }

  /** Unique, lower-cased tickers in the balance-check response. */
  private uniqueTickers(lines: BalanceCheckLine[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const l of lines) {
      const t = (l.asset_symbol ?? '').trim();
      if (!t) continue;
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
    return out;
  }

  /**
   * Fetch unit prices for the given tickers on `date` in the org's base
   * currency by issuing a single batched GraphQL request that aliases one
   * `historicPrices` query per ticker. One HTTP round-trip regardless of
   * ticker count, sidestepping the browser's per-origin connection cap.
   */
  private async fetchPriceMap(tickers: string[], date: string): Promise<Map<string, number>> {
    const map = new Map<string, number>();
    // Drop placeholders the pricing service doesn't recognise; otherwise a
    // single 'Coin not found' from the resolver pollutes the whole batch.
    tickers = tickers.filter((t) => this.isLookupableTicker(t));
    if (!tickers.length) return map;

    const orgId = this.$store.state.currentOrg.id;
    const from = moment.utc(date).startOf('day').unix();
    const to = moment.utc(date).endOf('day').unix();

    const varDecls = ['$orgId: ID!', '$from: Int', '$to: Int', ...tickers.map((_, i) => `$c${i}: String!`)].join(', ');
    const aliasFields = tickers
      .map(
        (_, i) => `
        p${i}: historicPrices(orgId: $orgId, coin: $c${i}, fromTimestampSec: $from, toTimestampSec: $to, pageSize: 1) {
          prices {
            ... on HistoricPriceSuccess {
              price {
                ... on PriceDetailCandlestick { price }
                ... on PriceDetailOverride { price }
                ... on PriceDetailCoarseGrain { price }
              }
            }
          }
        }`
      )
      .join('\n');
    const query = gql`query BalanceCheckHistoricPrices(${varDecls}) {${aliasFields}\n}`;

    const variables: Record<string, unknown> = { orgId, from, to };
    tickers.forEach((t, i) => {
      variables[`c${i}`] = t;
    });

    // `errorPolicy: 'all'` so a single unknown coin (e.g. address-svc says
    // "Coin not found") doesn't fail the whole batch — Apollo just returns
    // null for that alias and we skip it below.
    const res = await this.$apollo.query({
      query,
      variables,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });
    const data: any = (res as any).data ?? {};
    tickers.forEach((t, i) => {
      const node = data[`p${i}`];
      const first = node?.prices?.[0];
      const num = this.toNum(first?.price?.price);
      if (Number.isFinite(num)) {
        map.set(t.toLowerCase(), num);
      }
    });
    return map;
  }

  /**
   * Internal/placeholder tickers (e.g. `COIN.4212`, `COIN.FIAT.1`) that
   * address-svc and the pricing service don't recognise. Skipping them up
   * front avoids a flood of 404s and pointless work.
   */
  private isLookupableTicker(ticker: string): boolean {
    if (!ticker) return false;
    if (/^COIN\./i.test(ticker)) return false;
    return true;
  }

  /**
   * Hit address-svc once per ticker (in parallel) to read `spamScore`. Any
   * ticker with `spamScore > 0` ends up in the returned set. Failures are
   * treated as non-spam so the report still renders.
   */
  private async fetchSpamSet(
    tickers: string[],
    onProgress?: (done: number, total: number) => void
  ): Promise<Set<string>> {
    const set = new Set<string>();
    if (!tickers.length || !baConfig.addressSvcUrl) {
      onProgress?.(0, 0);
      return set;
    }
    const candidates = tickers.filter((t) => this.isLookupableTicker(t));
    const total = candidates.length;
    let done = 0;
    onProgress?.(0, total);

    // address-svc has no batch endpoint, so we fan out one GET per ticker.
    // Browsers cap at ~6 concurrent connections per origin which serializes
    // this anyway — the per-promise progress callback lets us surface
    // status while the queue drains.
    const results = await Promise.all(
      candidates.map(async (t) => {
        try {
          const { data } = await axios.get(`${baConfig.addressSvcUrl}/symbols/${encodeURIComponent(t)}`);
          const score = typeof data?.spamScore === 'number' ? data.spamScore : 0;
          done += 1;
          onProgress?.(done, total);
          return { t, score };
        } catch {
          done += 1;
          onProgress?.(done, total);
          return { t, score: 0 };
        }
      })
    );
    for (const r of results) {
      if (r.score > 0) set.add(r.t.toLowerCase());
    }
    return set;
  }

  public get totalLines(): number {
    return this.balanceCheckReport?.lines?.length ?? 0;
  }

  public get mismatchCount(): number {
    return (this.balanceCheckReport?.lines ?? []).filter((l) => this.isMismatch(l)).length;
  }

  public get totalAbsDiff(): number {
    return (this.balanceCheckReport?.lines ?? []).reduce((sum, l) => {
      const v = this.toNum(l.register_difference ?? l.difference);
      return sum + (Number.isFinite(v) ? Math.abs(v) : 0);
    }, 0);
  }

  public get stats() {
    return [{ label: 'As of', value: this.compareDate || '—' }];
  }

  /**
   * Single status line surfaced inside the loading placeholder. Picks
   * the currently-active stage from `loadingStages` and formats it as
   * `Label… done / total` (when counted) or just `Label…` otherwise.
   * Falls back to `Running report…` when no stage is active yet (e.g.
   * before the first stage has been marked active).
   */
  public get loadingStatusLine(): string {
    const active = this.loadingStages.find((s) => s.status === 'active');
    if (!active) return 'Running report…';
    if (active.progress && active.progress.total > 0) {
      return `${active.label}… ${active.progress.done} / ${active.progress.total}`;
    }
    return `${active.label}…`;
  }

  public get statusText(): string {
    if (this.isLoading) {
      // Pick the first currently-active stage to surface beside the button.
      // The full multi-stage view lives in the loading placeholder; this is
      // the condensed version.
      const active = this.loadingStages.find((s) => s.status === 'active');
      if (active) {
        const label = active.label;
        if (active.progress && active.progress.total > 0) {
          return `${label} (${active.progress.done}/${active.progress.total})`;
        }
        return active.detail ? `${label} · ${active.detail}` : label;
      }
      return 'Running…';
    }
    if (!this.reportElapsedTime) return '';
    return `Ran in ${this.reportElapsedTime}s · ${this.totalLines} rows`;
  }

  /**
   * True when both the Bitwave balance and the third-party balance are
   * present and exactly 0. A missing third-party balance does not count as
   * 0 — those rows stay visible regardless of this filter.
   */
  public isBothBalancesZero(line: BalanceCheckLine): boolean {
    if (!this.hasThirdPartyBalance(line)) return false;
    const bw = this.toNum(line.bitwave_register_balance ?? line.bitwave_balance);
    const tp = this.toNum(line.thirdparty_balance);
    if (!Number.isFinite(bw) || !Number.isFinite(tp)) return false;
    return bw === 0 && tp === 0;
  }

  /** A line is only a mismatch if there's a third-party balance to compare against. */
  public isMismatch(line: BalanceCheckLine): boolean {
    if (!this.hasThirdPartyBalance(line)) return false;
    const diff = this.toNum(line.register_difference ?? line.difference);
    if (!Number.isFinite(diff)) return false;
    return Math.abs(diff) > 0;
  }

  /**
   * Token-difference × unit-price for the line's ticker. Returns null when we
   * don't have a third-party balance to compare against, or when no price was
   * returned for the ticker.
   */
  public getDollarVariance(line: BalanceCheckLine): number | null {
    if (!this.hasThirdPartyBalance(line)) return null;
    if (!this.unitPrices) return null;
    const ticker = (line.asset_symbol ?? '').toLowerCase().trim();
    if (!ticker) return null;
    const price = this.unitPrices.get(ticker);
    if (price == null || !Number.isFinite(price)) return null;
    const diff = this.toNum(line.register_difference ?? line.difference);
    if (!Number.isFinite(diff)) return null;
    return diff * price;
  }

  /** Signed sum of $ variance for the rows currently visible in the table. */
  public get visibleDollarVariance(): number {
    return this.visibleRows.reduce((sum, r) => sum + (r.dollarVariance ?? 0), 0);
  }

  /** Format a numeric value as "$1,234.56". Returns '—' for null/NaN. */
  public formatDollar(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${sign}${this.fiatSymbol}${abs}`;
  }

  /**
   * Returns true when the third-party balance is present and numeric. Treats
   * `null` / `undefined` / `''` as missing. `0` is a valid balance and counts
   * as present.
   */
  public hasThirdPartyBalance(line: BalanceCheckLine): boolean {
    const v = line?.thirdparty_balance;
    if (v === null || v === undefined) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    return Number.isFinite(this.toNum(v));
  }

  public getTimeValue(field: string | { value: string } | undefined): string {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field.value ?? '';
  }

  public isValidDate(dateString: string): boolean {
    const dateObj = new Date(dateString);
    return !isNaN(dateObj.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  }

  private toNum(v: unknown): number {
    if (v == null) return NaN;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return Number(v.replace(/,/g, ''));
    return NaN;
  }

  /**
   * Display helper: numeric value to 4 decimal places, with thousands separators.
   * Returns an empty string for null/NaN so the cell stays blank.
   */
  public formatCurrency(value: unknown): string {
    const n = this.toNum(value);
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    });
  }

  /** Format an ISO timestamp in the org's configured timezone. */
  public toBalanceTimeStr(isoString: string): string {
    if (!isoString) return '';
    const tz = this.$store.state.currentOrg?.timezone;
    const m = tz ? moment(isoString).tz(tz) : moment(isoString);
    return m.format('MM-DD-YYYY HH:mm:ss');
  }

  /** Seconds (string, 2dp) elapsed since the given epoch ms. */
  public getElapsedTime(startTime: number): string {
    return ((Date.now() - startTime) / 1000).toFixed(2);
  }

  public handleDownloadReport() {
    if (!this.balanceCheckReport) return;
    const orgId = this.$store.state.currentOrg.id;

    const csv = this.buildVisibleCsv();
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `balance_check_report_${orgId}_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Build a CSV that mirrors what the user sees in the table:
   *   - same columns, same order (incl. Bitwave Balance Time before
   *     Third-Party Balance Time)
   *   - same filters applied (wallet/asset text filter, mismatches-only,
   *     hide-spam, hide-zero-balances) and same sort
   *   - same formatting (currency precision, $ variance, N/A for missing
   *     third-party balance, timestamps in the org's timezone)
   */
  private buildVisibleCsv(): string {
    const header = this.columns.map((c) => c.heading);
    const rows = this.visibleLines.map((line) => [
      line.wallet_name ?? '',
      this.getConnection(line).label === '—' ? '' : this.getConnection(line).label,
      line.asset_symbol ?? '',
      this.formatCurrency(line.bitwave_register_balance ?? line.bitwave_balance),
      this.hasThirdPartyBalance(line) ? this.formatCurrency(line.thirdparty_balance) : 'N/A',
      this.hasThirdPartyBalance(line) ? this.formatCurrency(line.register_difference ?? line.difference) : '',
      this.hasThirdPartyBalance(line) && this.getDollarVariance(line) !== null
        ? this.formatDollar(this.getDollarVariance(line))
        : '',
      this.toBalanceTimeStr(this.getTimeValue(line.bitwave_balance_time)),
      this.toBalanceTimeStr(this.getTimeValue(line.thirdparty_balance_time)),
    ]);
    return [header, ...rows].map((r) => r.map((c) => this.csvEscape(c)).join(',')).join('\n');
  }

  /**
   * RFC-4180-ish CSV escaping: wrap in quotes when the cell contains a
   * comma, quote, or newline; escape embedded quotes by doubling them.
   */
  private csvEscape(value: unknown): string {
    const s = value == null ? '' : String(value);
    if (/[",\n\r]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  }

  /**
   * Cancel an in-flight report run. Bumps the request id (so the still-
   * pending network call won't commit when it eventually resolves),
   * stops the spinner, and tears down the staged-progress state. We
   * deliberately don't abort the underlying fetch — the backend will
   * complete on its own and the response is simply discarded.
   */
  public handleCancelReport(): void {
    this.runRequestId += 1;
    this.isLoading = false;
    this.loadingStages = [];
    this.stopTipRotation();
  }

  public async handleRunReport() {
    if (!this.isValid || this.isLoading) return;
    const requestId = ++this.runRequestId;
    this.isLoading = true;
    this.errorMessage = '';
    this.reportElapsedTime = null;
    // Clear any prior render so the loading placeholder takes over while
    // we enrich the new run. Without this the previous report stays
    // visible during the spam/pricing wait, which is misleading.
    this.balanceCheckReport = null;
    this.spamTickers = null;
    this.unitPrices = null;

    // Reset the stage checklist for this run.
    this.loadingStages = [
      { key: 'balanceCheck', label: 'Running balance check', status: 'active' },
      { key: 'spam', label: 'Classifying spam', status: 'pending' },
      { key: 'pricing', label: 'Fetching historic prices', status: 'pending' },
    ];
    this.startTipRotation();

    const orgId = this.$store.state.currentOrg.id;
    const params = new URLSearchParams({
      endDate: this.compareDate,
      exportResults: 'true',
    });
    const exportUrl = `${this.baseUrl}orgs/${orgId}/inventory-views/reports/balance-check-report?${params.toString()}`;

    const reportStartTime = Date.now();
    let report: { lines?: BalanceCheckLine[] };
    let reportCsv = '';
    const balanceCheckStart = Date.now();
    try {
      // 1. Run the balance-check report (the original endpoint).
      // eslint-disable-next-line no-console
      console.time('[BalanceCheck] balance-check-report');
      const resp = await axios({
        method: 'get',
        url: exportUrl,
        withCredentials: true,
        // No client-side timeout: this report can legitimately take a long
        // time for large orgs / wide date ranges. Let the backend decide.
        timeout: 0,
      });
      // eslint-disable-next-line no-console
      console.timeEnd('[BalanceCheck] balance-check-report');
      report = resp.data?.metadata ?? { lines: [] };
      reportCsv = resp.data?.csv ?? '';
      this.markStageDone('balanceCheck', `${this.getElapsedTime(balanceCheckStart)}s`);
    } catch (error: any) {
      // If the user cancelled mid-flight, swallow the error and bail.
      if (requestId !== this.runRequestId) return;
      this.errorMessage = error?.message ?? 'Failed to run report';
      this.showErrorMessage();
      this.isLoading = false;
      this.loadingStages = [];
      this.stopTipRotation();
      return;
    }

    // Cancelled while balance-check was in flight: discard the response.
    if (requestId !== this.runRequestId) return;

    // 2. Enrich with spam classification + pricing BEFORE rendering, so
    //    the table never flashes spam rows that then disappear. Both are
    //    non-fatal: failures just disable Hide spam / $ Variance.
    const tickers = this.uniqueTickers(report.lines ?? []);
    // eslint-disable-next-line no-console
    console.debug(`[BalanceCheck] unique tickers: ${tickers.length}`);
    // eslint-disable-next-line no-console
    console.time('[BalanceCheck] spam+pricing');

    // Spam + pricing run in parallel; mark both active so the user sees
    // two concurrent rows in the checklist.
    this.setStageActive('spam', tickers.length ? `0 / ${tickers.length}` : undefined);
    if (tickers.length) {
      const idx = this.loadingStages.findIndex((s) => s.key === 'spam');
      if (idx >= 0)
        this.$set(this.loadingStages, idx, {
          ...this.loadingStages[idx],
          progress: { done: 0, total: tickers.length },
        });
    }
    this.setStageActive('pricing');

    const spamStart = Date.now();
    const pricingStart = Date.now();
    const [spamSet, rawPrices] = await Promise.all([
      this.fetchSpamSet(tickers, (done, total) => {
        const idx = this.loadingStages.findIndex((s) => s.key === 'spam');
        if (idx < 0) return;
        this.$set(this.loadingStages, idx, {
          ...this.loadingStages[idx],
          detail: `${done} / ${total}`,
          progress: { done, total },
        });
      })
        .then((s) => {
          this.markStageDone(
            'spam',
            tickers.length
              ? `${tickers.length} ticker${tickers.length === 1 ? '' : 's'} · ${this.getElapsedTime(spamStart)}s`
              : 'skipped'
          );
          return s;
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.warn('Spam classification failed; Hide spam disabled for this run.', e);
          this.markStageDone('spam', 'unavailable');
          return new Set<string>();
        }),
      this.fetchPriceMap(tickers, this.compareDate)
        .then((p) => {
          this.markStageDone('pricing', `${this.getElapsedTime(pricingStart)}s`);
          return p;
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.warn('Pricing fetch failed; $ Variance disabled for this run.', e);
          this.markStageDone('pricing', 'unavailable');
          return new Map<string, number>();
        }),
    ]);
    // eslint-disable-next-line no-console
    console.timeEnd('[BalanceCheck] spam+pricing');

    // Cancelled while spam/pricing were enriching: discard everything.
    if (requestId !== this.runRequestId) return;

    // Reconcile: drop any price entries the spam check marked as spam.
    for (const t of spamSet) rawPrices.delete(t);

    // 3. Commit everything at once. The watcher-free design here matters:
    //    setting `balanceCheckReport` last guarantees the table mounts
    //    only after spam + prices are already in state.
    this.spamTickers = spamSet;
    this.unitPrices = rawPrices;
    this.reportCSV = reportCsv;
    this.reportElapsedTime = this.getElapsedTime(reportStartTime);
    this.generatedAt = moment().format('M/D/YYYY, h:mm:ss A');
    this.balanceCheckReport = report;

    this.isLoading = false;
    this.loadingStages = [];
    this.stopTipRotation();
  }

  /** Flip a stage to `active` and set its detail (optional). */
  private setStageActive(key: 'balanceCheck' | 'spam' | 'pricing', detail?: string): void {
    const idx = this.loadingStages.findIndex((s) => s.key === key);
    if (idx < 0) return;
    this.$set(this.loadingStages, idx, { ...this.loadingStages[idx], status: 'active', detail });
  }

  /** Flip a stage to `done` and set its detail (e.g. elapsed time). */
  private markStageDone(key: 'balanceCheck' | 'spam' | 'pricing', detail?: string): void {
    const idx = this.loadingStages.findIndex((s) => s.key === key);
    if (idx < 0) return;
    this.$set(this.loadingStages, idx, { ...this.loadingStages[idx], status: 'done', detail, progress: undefined });
  }

  /**
   * The currently-displayed tip. Bound by the template. Returns the
   * empty string when no tips are configured (defensive).
   */
  public get currentLoadingTip(): string {
    if (!this.loadingTips.length) return '';
    return this.loadingTips[this.loadingTipIndex % this.loadingTips.length];
  }

  /** Start cycling tips every 5 seconds. Safe to call multiple times. */
  private startTipRotation(): void {
    this.stopTipRotation();
    this.loadingTipIndex = 0;
    this.tipInterval = window.setInterval(() => {
      this.loadingTipIndex = (this.loadingTipIndex + 1) % this.loadingTips.length;
    }, 5000);
  }

  /** Clear the tip rotator interval if any. */
  private stopTipRotation(): void {
    if (this.tipInterval !== null) {
      window.clearInterval(this.tipInterval);
      this.tipInterval = null;
    }
  }

  showErrorMessage() {
    this.$store.commit(MUT_SNACKBAR, {
      color: 'error',
      message: 'Failed to run report',
    });
  }

  public beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown, true);
    window.removeEventListener('keydown', this.onWindowKeydown);
    this.stopTipRotation();
  }

  /**
   * Ensure wallets are loaded so the Connection column can populate. We only
   * fetch if the store doesn't already have them \u2014 this won't run the report.
   */
  private onWindowKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.openFilter) {
      this.openFilter = null;
    }
  };

  public mounted() {
    // Capture phase so downstream stopPropagation (e.g. inside other Vue
    // components on the page) can't prevent the popover from closing.
    document.addEventListener('mousedown', this.onDocumentMousedown, true);
    window.addEventListener('keydown', this.onWindowKeydown);
  }
}
